import React, { useEffect, useState, useRef, useContext } from "react";
import { Zap } from "lucide-react";
import Confetti from "react-confetti";
import { progressService } from "../../../../../services/progressService.js";
import { UserContext } from "../../../../../../src/UserContext.js";
import starsCollection from "../../../../../assets/starsCollection.png";
import { MdLeaderboard } from "react-icons/md";


const CelebrationScreen = ({ userId, subsectionId, onNextStep }) => {
  const service = progressService;

  const [tasks, setTasks] = useState([]);
  // const [showFirstCard, setShowFirstCard] = useState(true);
  const [showFirstCard] = useState(true);
  const [showSecondCard, setShowSecondCard] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  const loadedRef = useRef({ userId: null, subsectionId: null, done: false });
  const { user } = useContext(UserContext);
  const userName = user?.firstName ? `${user.firstName}` : "";

  useEffect(() => {
    // Set window size for confetti
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let mounted = true;

    if (!userId || !subsectionId) return;

    if (
      loadedRef.current.done &&
      loadedRef.current.userId === userId &&
      loadedRef.current.subsectionId === subsectionId
    ) {
      return;
    }

    (async () => {
      if (!service || typeof service.getUserProgress !== "function") {
        console.warn("progressService.getUserProgress not available.");
        return;
      }

      try {
        const saved = await service.getUserProgress(userId, subsectionId);
        const savedTasks = saved?.data?.tasks ?? saved?.tasks ?? null;
        if (!mounted) return;

        if (Array.isArray(savedTasks)) {
          const normalized = savedTasks.map((t, idx) => ({
            id: t.id ?? idx + 1,
            name: t.name ?? `Task ${idx + 1}`,
            energy: typeof t.energy === "number" ? t.energy : t.energy ? Number(t.energy) : null,
            category: t.category ?? null,
            delegationQuadrant: t.delegationQuadrant ?? null,
            subtasks: (t.subtasks || []).map((s, sidx) => ({
              id: s.id ?? `${Date.now()}-${sidx}`,
              name: s.name ?? `Subtask ${sidx + 1}`,
              energy: typeof s.energy === "number" ? s.energy : s.energy ? Number(s.energy) : null,
              category: s.category ?? null,
              delegationQuadrant: s.delegationQuadrant ?? null,
            })),
          }));

          setTasks(normalized);
          loadedRef.current = { userId, subsectionId, done: true };
        }
      } catch (err) {
        console.warn("Error loading tasks from progressService:", err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [userId, subsectionId, service]);

  // Timing logic — shortened to behave like the provided mock
  useEffect(() => {
    // Show second card shortly after (1.8s)
    const timer1 = setTimeout(() => {
      setShowSecondCard(true);
    }, 6000);

    // Stop confetti after ~4s
    const timer2 = setTimeout(() => {
      setShowConfetti(false);
    }, 8000);

    // Auto-advance after ~7s
    const timer3 = setTimeout(() => {
      onNextStep?.();
    }, 10000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onNextStep]);

  // Get tasks for each quadrant
  const getFocusTasks = () => {
    const items = [];
    tasks.forEach((t) => {
      if (t.delegationQuadrant === "focus_yourself") {
        items.push(t);
      }
      (t.subtasks || []).forEach((s) => {
        if (s.delegationQuadrant === "focus_yourself") {
          items.push({ ...s, isSubtask: true });
        }
      });
    });
    return items;
  };

  const getDelegateTasks = () => {
    const items = [];
    tasks.forEach((t) => {
      if (t.delegationQuadrant === "delegate_monitor") {
        items.push(t);
      }
      (t.subtasks || []).forEach((s) => {
        if (s.delegationQuadrant === "delegate_monitor") {
          items.push({ ...s, isSubtask: true });
        }
      });
    });
    return items;
  };

  const calculateEnergy = (taskList) => {
    return taskList.reduce((sum, task) => {
      const energy = typeof task.energy === "number" ? task.energy : 0;
      return sum + energy;
    }, 0);
  };

  const focusTasks = getFocusTasks();
  const delegateTasks = getDelegateTasks();
  const focusEnergy = calculateEnergy(focusTasks);
  const delegateEnergy = calculateEnergy(delegateTasks);

  const getCategoryBadge = (category) => {
    if (!category) return null;

    const badgeConfig = {
      leverage: { text: "L", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
      neutral: { text: "N", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
      overhead: { text: "O", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
    };

    const config = badgeConfig[category];
    if (!config) return null;

    return (
      <span
        className={`${config.bg} ${config.border} border rounded-full px-2 py-1 text-[10px] font-gotham font-medium text-[#000000] flex items-center gap-1`}
      >
        <span className="flex flex-col gap-[1px]">
          <MdLeaderboard className="text-[#1B7977] text-16" />
          {/* <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" />
          <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" />
          <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" /> */}
        </span>
        {config.text}
      </span>
    );
  };

    const TaskCard = ({ title, subtitle, tasks, bgColor, textColor, roi, energy }) => {
    const isDelegate = String(title).toLowerCase().includes("delegate");

    // header style: if bgColor provided use it, else use default teal.
    const headerStyle = bgColor
        ? { background: undefined } // we apply classes conditionally below instead of inline gradient so tailwind works
        // : { background: "linear-gradient(180deg,#2E9B99,#1B7977)" };
        : { background: "#1B7977)" };

    return (
        <div
        className={`max-w-[420px] min-h-[550px] w-full mx-auto rounded-[12px] shadow-md overflow-hidden bg-white ${
            isDelegate ? "border border-transparent" : ""
        }`}
        >
        {/* Header */}
        <div
            className={`w-full ${
            isDelegate
                ? "bg-gradient-to-b from-[#101010] to-[#000000] border-b border-[#1f1f1f]"
                : "bg-gradient-to-br from-[#2E9B99] to-[#1B7977]"
            }`}
            style={headerStyle}
        >
            <div className="px-6 py-6 text-center relative">
              <img src={starsCollection} alt="Stars" className="w-5 h-5 lg:w-8 lg:h-8 absolute left-[8%] top-[30%]" />
            <div className="flex items-center justify-center gap-3 mb-2">
                {/* <img src={starsCollection} alt="Stars" className="w-5 h-5" /> */}
                <div
                className={`text-[11.06px] uppercase tracking-wider text-center font-gotham font-medium ${
                    isDelegate ? "text-[#82CDCC]" : "text-[#82CDCC]"
                }`}
                >
                {subtitle}
                </div>
                {/* <img
                src={starsCollection}
                alt="Stars"
                className={`w-5 h-5 ${isDelegate ? "rotate-0 opacity-90" : "rotate-180"}`}
                /> */}
            </div>
              <img src={starsCollection} alt="Stars" className="w-5 h-5 lg:w-8 lg:h-8 absolute right-[8%] top-[30%]" />

            <h3
                className={`${
                isDelegate ? "text-[#FFFFFF] text-[20.09px]" : "text-white text-[20.09px]"
                } font-gotham font-medium leading-tight`}
            >
                {title}
            </h3>
            </div>
        </div>

        {/* Body */}
        <div className="p-6 bg-[#F8F8F8] h-full relative">
            <div className="rounded-[10px]">
            <div className="space-y-3">
                {tasks.slice(0, 4).map((task, idx) => (
                <div
                    key={idx}
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-[9.82px] bg-white border border-[#DEDEDE] shadow-sm"
                >
                    {/* drag dots + name */}
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                    <div className="flex flex-col gap-[1.5px] opacity-60">
                        <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
                        <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
                        <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
                    </div>
                        <div className="flex flex-col gap-[1.5px] opacity-60">
                        <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
                        <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
                        <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
                    </div>

                    <div className="text-[15.71px] font-gotham font-medium text-[#000000] truncate ml-2">
                        {task.name}
                    </div>
                    </div>

                    {/* right: energy pill + category badge */}
                    <div className="flex items-center gap-2">
                    <div className="bg-[#FAFAFA] border border-[#EEF7F6] rounded-full px-3 py-1 flex items-center gap-2">
                        <span className="text-[12px] text-[#000000] font-medium font-gotham">
                        {task.energy ?? "-"}%
                        </span>
                        <Zap className="w-3 h-3 text-[#54B0AF]" stroke="none" fill="#54B0AF" />
                    </div>
                    {getCategoryBadge(task.category)}
                    </div>
                </div>
                ))}
            </div>
            </div>

            {/* big spacer so ROI sits near bottom like image */}
            <div className="h-24" />

            {/* ROI */}
            <div className="text-center absolute top-[65%] left-[30%] ">
            <div
                className={`${
                isDelegate ? "text-[#DADADA] text-[42.21px] font-gotham font-bold leading-none" : "text-[#DADADA] text-[42.21px] font-gotham font-bold leading-none"
                }`}
            >
                {roi}
            </div>
            <div className="text-[#DADADA] text-[14px] font-gotham uppercase tracking-wider">
                {energy}% ENERGY SPEND
            </div>
            </div>
        </div>
        </div>
    );
    };

  return (
    <div className="min-h-screen flex flex-col items-center px-6 relative overflow-hidden">
      {/* Confetti */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={200}
          recycle={true}
          colors={["#54B0AF", "#2E8B87", "#87BEB8", "#DFF3F1", "#FFD700", "#FFA500"]}
        />
      )}

      {/* Main content */}
      <div className="relative z-10 max-w-6xl mx-auto mt-4">
        {/* Header text */}
        <div className="text-center mb-8 px-4">
          <h1 className="text-[32px] font-gotham font-medium text-[#323232] leading-tight mb-0.5">
            Amazing {userName}! You've done something that
          </h1>
          <h2 className="text-[32px] font-gotham font-medium text-[#323232] leading-tight mb-0.5">
            only 1% of the hired workplace does –{" "}
            <span className="text-[#54B0AF] relative">
              <span className="absolute -bottom-1 left-0 w-full h-3 bg-[#54B0AF] opacity-30 rounded-full" />
            </span>
          </h2>
          <h3 className="text-[32px] font-gotham font-medium text-[#323232]">
            Identified high leverage tasks and delegation tasks
          </h3>
        </div>

        {/* Cards container */}
        <div
          className={`grid gap-8 transition-all duration-700 ${
            showSecondCard ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 max-w-md mx-auto"
          }`}
        >
          {/* Focus Card */}
          {showFirstCard && (
            <TaskCard
              title="Focus deeply yourself"
              subtitle="KEY TASKS THAT YOU SHOULD"
              tasks={focusTasks}
              bgColor="bg-gradient-to-br from-[#2E9B99] to-[#1B7977]"
              textColor="text-[#A8E6E3]"
              roi="10x ROI"
              energy={focusEnergy}
            />
          )}

          {/* Delegate Card */}
          {showSecondCard && (
            <TaskCard
              title="Delegate to Your team"
              subtitle="KEY TASKS THAT YOU SHOULD"
              tasks={delegateTasks}
              bgColor="bg-gradient-to-br from-[#2C2C2C] to-[#000000]"
              textColor="text-[#A0A0A0]"
              roi="1x ROI"
              energy={delegateEnergy}
            />
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CelebrationScreen;